'use client'

import { useMemo, useState, useTransition } from 'react'
import TeamMemberCard from '@/components/TeamMemberCard'
import FilterButton from '@/components/ui/FilterButton'
import SectionHeader from '@/components/ui/SectionHeader'
import { getBackgroundFromShade } from '@/utilities/sectionSettings'
import type { Team } from '@/payload-types'

type FilterKey = 'all' | 'ansprechpersonen'

type LocationKey = 'Kriessern' | 'Domat/Ems'

interface TeamByLocationClientProps {
  teamMembers: Team[]
  header: {
    infoTagText?: string | null
    title: string
    subtitle?: string | null
  }
  settings?: {
    anchorId?: string
    backgroundShade?: 'white' | 'neutral-50' | 'neutral-100'
    isDarkMode?: boolean
    padding?: 'sm' | 'md' | 'lg' | 'xl'
    container?: 'container' | 'narrow' | 'full'
  }
}

function sortByLastname(members: Team[]): Team[] {
  return [...members].sort((a, b) => {
    const lastnameA = (a.lastname || '').toLowerCase()
    const lastnameB = (b.lastname || '').toLowerCase()
    return lastnameA.localeCompare(lastnameB, 'de', { sensitivity: 'base' })
  })
}

function matchesSearch(member: Team, query: string): boolean {
  const q = query.toLowerCase()
  const fullName = `${member.prename} ${member.lastname}`.toLowerCase()
  const description = (member.description || '').toLowerCase()
  const email = (member.email || '').toLowerCase()
  return fullName.includes(q) || description.includes(q) || email.includes(q)
}

export default function TeamByLocationClient({
  teamMembers,
  header,
  settings,
}: TeamByLocationClientProps) {
  const [filter, setFilter] = useState<FilterKey>('ansprechpersonen')
  const [searchQuery, setSearchQuery] = useState('')
  const [, startTransition] = useTransition()

  const handleFilterChange = (newFilter: FilterKey) => {
    setFilter(newFilter)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    startTransition(() => {
      setSearchQuery(e.target.value)
    })
  }

  const groups = useMemo(() => {
    const gl = teamMembers.filter((m) => m.isManagement)

    const byLocation: Record<LocationKey, Team[]> = {
      Kriessern: teamMembers.filter((m) => m.place === 'Kriessern' && !m.isManagement) as Team[],
      'Domat/Ems': teamMembers.filter((m) => m.place === 'Domat/Ems' && !m.isManagement) as Team[],
    }

    function orderGroup(members: Team[]): Team[] {
      const an = members.filter((m) => m.isContactPerson)
      const others = members.filter((m) => !m.isContactPerson)
      return [...sortByLastname(an), ...sortByLastname(others)]
    }

    function applySearch(members: Team[]): Team[] {
      if (!searchQuery.trim()) return members
      return members.filter((m) => matchesSearch(m, searchQuery))
    }

    const glVisible =
      filter === 'all' ? orderGroup(gl) : sortByLastname(gl.filter((m) => m.isContactPerson))
    const kriessern =
      filter === 'all'
        ? orderGroup(byLocation['Kriessern'])
        : sortByLastname(byLocation['Kriessern'].filter((m) => m.isContactPerson))
    const domatEms =
      filter === 'all'
        ? orderGroup(byLocation['Domat/Ems'])
        : sortByLastname(byLocation['Domat/Ems'].filter((m) => m.isContactPerson))

    return {
      gl: applySearch(glVisible),
      kriessern: applySearch(kriessern),
      domatEms: applySearch(domatEms),
    }
  }, [filter, searchQuery, teamMembers])

  const _background = getBackgroundFromShade(settings?.backgroundShade)
  const _padding = settings?.padding || 'md'

  const hasResults = groups.gl.length > 0 || groups.kriessern.length > 0 || groups.domatEms.length > 0

  return (
    <div>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between md:gap-6 mb-8">
        <SectionHeader
          infoTagText={header.infoTagText ?? ''}
          title={header.title}
          subtitle={header.subtitle ?? undefined}
          align="left"
          size="md"
          className="mb-0"
        />
      </div>

      {/* Filter + Search */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mb-8">
        <div className="flex items-center gap-3 flex-shrink-0">
          <FilterButton
            label="Alle"
            isActive={filter === 'all'}
            onClick={() => handleFilterChange('all')}
            variant="outline"
            size="md"
          />
          <FilterButton
            label="Ansprechpersonen"
            isActive={filter === 'ansprechpersonen'}
            onClick={() => handleFilterChange('ansprechpersonen')}
            variant="outline"
            size="md"
          />
        </div>
        <div className="relative flex-1 sm:max-w-md">
          <svg
            className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-text-brand pointer-events-none"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
            />
          </svg>
          <input
            type="search"
            placeholder="Suche nach Name, Funktion oder E-Mail…"
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full px-4 py-3 pl-10 border border-border-brand bg-background text-base text-text-primary text-text-brand uppercase font-ui font-normal placeholder:text-text-secondary/60 placeholder:normal-case transition-all duration-300 focus:outline-none focus:border-border-brand focus:ring-1 focus:ring-border-brand"
          />
        </div>
      </div>

      {/* Geschäftsleitung */}
      {groups.gl.length > 0 && (
        <div className="mt-8">
          <h3 className="heading-xs text-primary mb-6 uppercase">Geschäftsleitung</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center sm:justify-items-start">
            {groups.gl.map((m, i) => (
              <TeamMemberCard key={m.id} teamMember={m} index={i} />
            ))}
          </div>
        </div>
      )}

      {/* Kriessern */}
      {groups.kriessern.length > 0 && (
        <div className="mt-12">
          <h3 className="heading-xs mb-6 uppercase">Kriessern</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center sm:justify-items-start">
            {groups.kriessern.map((m, i) => (
              <TeamMemberCard key={m.id} teamMember={m} index={i} />
            ))}
          </div>
        </div>
      )}

      {/* Domat/Ems */}
      {groups.domatEms.length > 0 && (
        <div className="mt-12">
          <h3 className="heading-xs mb-6 uppercase">Domat/Ems</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center sm:justify-items-start">
            {groups.domatEms.map((m, i) => (
              <TeamMemberCard key={m.id} teamMember={m} index={i} />
            ))}
          </div>
        </div>
      )}

      {/* No results */}
      {!hasResults && searchQuery.trim() && (
        <p className="text-center text-text-secondary body-base py-12">
          Keine Ergebnisse für &laquo;{searchQuery.trim()}&raquo;
        </p>
      )}
    </div>
  )
}
